import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import campaignAdvertisingService from '../../services/campaignAdvertisingService';

const AddInfluencerPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaign, formData, requirement, advertisingId, status } = location.state || {};
  const advertisingStatus = status || 'published';
  const detailsRoute = '/company/dashboard/campaigns/advertising/add-details';
  const previewRoute = '/company/dashboard/campaigns/advertising/add-details/preview';
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!campaign || !formData) {
    navigate(-1);
    return null;
  }

  const detailRows = [
    { label: 'Requirement', value: requirement || '—', sectionId: 'requirement' },
    { label: 'Budget', value: formData.budget || '—', sectionId: 'budget' },
    { label: 'Brand Name', value: formData.brandName || '—', sectionId: 'brandName' },
    { label: 'Brand Manager', value: formData.brandManager?.name || '—', sectionId: 'brandManager' },
    { label: 'Contact - WhatsApp', value: formData.contactDetails?.whatsapp || '—', sectionId: 'contactDetails' },
    { label: 'Contact - Email', value: formData.contactDetails?.email || '—', sectionId: 'contactDetails' },
    { label: 'Contact - LinkedIn', value: formData.contactDetails?.linkedin || '—', sectionId: 'contactDetails' },
    { label: 'Categories', value: formData.category?.length ? formData.category.join(', ') : '—', sectionId: 'category' },
    { label: 'Number of Creators', value: formData.numberOfCreators || '—', sectionId: 'numberOfCreators' },
    { label: 'Deliverables', value: formData.deliverables || '—', sectionId: 'deliverables' },
    { label: 'Agency Name', value: formData.agencyName || '—', sectionId: 'agencyName' },
    { label: 'Gender', value: formData.gender || '—', sectionId: 'gender' },
    { label: 'Platforms', value: formData.platform?.length ? formData.platform.join(', ') : '—', sectionId: 'platform' },
    { label: 'Follower Range', value: formData.followerRange?.length ? formData.followerRange.join(', ') : '—', sectionId: 'followerRange' },
    { label: 'Locations', value: formData.location?.length ? formData.location.join(', ') : '—', sectionId: 'location' },
    { label: 'Languages', value: formData.language?.length ? formData.language.join(', ') : '—', sectionId: 'language' },
  ];

  const handleBack = () => {
    navigate(detailsRoute, {
      state: { campaign, formData, requirement, advertisingId, status: advertisingStatus },
      replace: true,
    });
  };

  const handleEditSection = (sectionId) => {
    navigate(detailsRoute, {
      state: { campaign, formData, requirement, focusSection: sectionId, returnTo: previewRoute, advertisingId, status: advertisingStatus },
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!requirement.trim()) {
      toast.warning('Requirement details missing');
      return;
    }

    if (!formData.brandManagerId) {
      toast.warning('Please select a brand manager for this campaign');
      return;
    }

    if (!formData.contactDetails?.whatsapp?.trim() || !formData.contactDetails?.email?.trim()) {
      toast.warning('Please add WhatsApp number and email in contact details');
      return;
    }

    if (!campaign?._id) {
      toast.error('Campaign information missing');
      return;
    }

    try {
      setIsSubmitting(true);
      const submissionData = {
        hireType: 'influencer',
        requirement,
        budget: formData.budget || '',
        budgetMeta: formData.budgetMeta || {},
        brandName: formData.brandName || '',
        brandManagerId: formData.brandManagerId || null,
        category: formData.category || [],
        numberOfCreators: formData.numberOfCreators || '',
        numberOfCreatorsMeta: formData.numberOfCreatorsMeta || {},
        deliverables: formData.deliverables || '',
        agencyName: formData.agencyName || '',
        gender: formData.gender || '',
        platform: formData.platform || [],
        followerRange: formData.followerRange || [],
        location: formData.location || [],
        language: formData.language || [],
        contactDetails: {
          whatsapp: formData.contactDetails?.whatsapp || '',
          email: formData.contactDetails?.email || '',
          linkedin: formData.contactDetails?.linkedin || '',
        },
        status: advertisingStatus,
      };

      if (advertisingId) {
        await campaignAdvertisingService.updateCampaignAdvertising(campaign._id, advertisingId, submissionData);
        toast.success('Requirement updated successfully!');
      } else {
        await campaignAdvertisingService.createCampaignAdvertising(campaign._id, submissionData);
        toast.success('Requirement posted successfully!');
      }
      navigate('/company/dashboard/campaigns/advertising/view', { replace: true });
    } catch (error) {
      console.error('Error submitting campaign advertising:', error);
      toast.error(error.message || 'Failed to post requirement');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-[40]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
          <h1 className="text-base sm:text-lg font-semibold text-gray-900">Preview Requirement</h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center space-x-3 mb-4">
            <FaCheckCircle className="text-green-500 text-2xl" />
            <div>
              <p className="text-sm text-gray-500">Campaign</p>
              <h2 className="text-xl font-semibold text-gray-900">{campaign.name || 'Selected Campaign'}</h2>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Review the details below before posting your requirement.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="divide-y divide-gray-100">
            {detailRows.map((row) => (
              <div key={row.label} className="p-4 sm:p-5">
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{row.label}</p>
                    <p className="text-sm sm:text-base text-gray-900 whitespace-pre-line break-words">{row.value}</p>
                  </div>
                  {row.sectionId && (
                    <button
                      onClick={() => handleEditSection(row.sectionId)}
                      className="text-xs font-semibold text-purple-600 hover:text-purple-700"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`w-full text-white font-semibold py-3 px-6 rounded-xl transition-colors ${
              isSubmitting ? 'bg-orange-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'
            }`}
          >
            {isSubmitting
              ? (advertisingId ? 'Updating...' : 'Posting...')
              : (advertisingId ? 'Update Requirement' : 'Post Requirement')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddInfluencerPreview;
